// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import {
  Award,
  GraduationCap,
  Loader2,
  Pencil,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { InlineDeleteConfirm } from '@/app/components/inline-delete-confirm';
import { BulkActionBar, SelectionCheckbox } from '@/app/components/bulk-action-bar';
import { PageHeader, FormCard, FieldHeader } from '@/app/components/page-layout';
import { AiSuggestionBox } from '@/app/components/ai-suggestion-box';
import { UnsavedChangesGuard } from '@/app/components/unsaved-changes-guard';
import type { BulkAction } from '@/app/components/bulk-action-bar';
import { useBulkSelection } from '@/app/hooks/useBulkSelection';
import { useUnsavedChanges } from '@/app/hooks/useUnsavedChanges';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  useEducation,
  useCreateEducation,
  useUpdateEducation,
  useDeleteEducation,
  useBulkDeleteEducation,
  usePolishEducation,
  educationSchema,
} from '@/app/features/education';
import type { EducationFormData } from '@/app/features/education';

type Education = {
  id: string;
  institution: string;
  degree?: string;
  field_of_study?: string;
  start_date?: string;
  end_date?: string;
  gpa?: string;
  description?: string;
  is_certification?: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};



export function EducationPage() {
  const education = useEducation();
  const createEducation = useCreateEducation();
  const updateEducation = useUpdateEducation();
  const deleteEducation = useDeleteEducation();
  const bulkDeleteEducation = useBulkDeleteEducation();
  const polishEducation = usePolishEducation();

  // AI polish state for saved entries: maps entryId → polished text (null = loading)
  const [polishResults, setPolishResults] = useState<
    Record<string, string | null>
  >({});

  // AI polish state for the open form (create or edit)
  const [formPolishing, setFormPolishing] = useState(false);
  const [formPolishResult, setFormPolishResult] = useState<string | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isCertification, setIsCertification] = useState(false);
  const [formData, setFormData] = useState<EducationFormData>({
    institution: '',
    degree: '',
    field_of_study: '',
    start_date: '',
    end_date: '',
    gpa: '',
    description: '',
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof EducationFormData, string>>
  >({});

  const unsaved = useUnsavedChanges(
    { ...formData, is_certification: isCertification },
    { enabled: !!editingId },
  );
  const editFormRef = useRef<HTMLDivElement>(null);

  const resetForm = useCallback(() => {
    setFormData({
      institution: '',
      degree: '',
      field_of_study: '',
      start_date: '',
      end_date: '',
      gpa: '',
      description: '',
    });
    setIsCertification(false);
    setFieldErrors({});
    setEditingId(null);
    setIsFormOpen(false);
    setFormPolishResult(null);
    setFormPolishing(false);
    unsaved.clear();
  }, [unsaved]);

  function openCreateForm() {
    resetForm();
    setIsFormOpen(true);
  }

  function openEditForm(entry: Education) {
    unsaved.guardClose(() => {
      const data: EducationFormData = {
        institution: entry.institution,
        degree: entry.degree ?? '',
        field_of_study: entry.field_of_study ?? '',
        start_date: entry.start_date ?? '',
        end_date: entry.end_date ?? '',
        gpa: entry.gpa ?? '',
        description: entry.description ?? '',
      };
      setFormData(data);
      setIsCertification(entry.is_certification ?? false);
      unsaved.snapshot({
        ...data,
        is_certification: entry.is_certification ?? false,
      });
      setFieldErrors({});
      setEditingId(entry.id);
      setIsFormOpen(true);
      // Scroll the inline form into view and focus its first input.
      setTimeout(() => {
        editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        editFormRef.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
      }, 50);
    });
  }

  function handleCloseForm() {
    unsaved.guardClose(resetForm);
  }

  function handleChange(field: keyof EducationFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    const result = educationSchema.safeParse(formData);
    if (!result.success) {
      const errors: Partial<Record<keyof EducationFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof EducationFormData;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    const onSuccess = () => {
      resetForm();
      toast.success('Education entry saved successfully.');
    };

    const payload = { ...result.data, is_certification: isCertification };
    if (editingId) {
      updateEducation.mutate(
        { ...payload, id: editingId },
        { onSuccess },
      );
    } else {
      createEducation.mutate(payload, { onSuccess });
    }
  }

  // AI polish of what is currently typed in the form (with an empty
  // description the AI drafts one from degree / field / institution).
  function runFormPolish() {
    setFormPolishing(true);
    setFormPolishResult(null);
    polishEducation.mutate(
      {
        id: editingId ?? undefined,
        institution: formData.institution,
        degree: formData.degree,
        field_of_study: formData.field_of_study,
        description: formData.description,
        is_certification: isCertification,
      },
      {
        onSuccess: (result) => {
          setFormPolishResult((result as { polished: string }).polished);
          setFormPolishing(false);
        },
        onError: () => setFormPolishing(false),
      },
    );
  }

  // AI polish on a saved entry — result shown in-place on the card
  function handlePolish(entry: Education) {
    setPolishResults((prev) => ({ ...prev, [entry.id]: null }));
    polishEducation.mutate(
      { id: entry.id },
      {
        onSuccess: (result) => {
          const polished = (result as { polished: string }).polished;
          setPolishResults((prev) => ({ ...prev, [entry.id]: polished }));
        },
        onError: () => {
          setPolishResults((prev) => {
            const next = { ...prev };
            delete next[entry.id];
            return next;
          });
        },
      },
    );
  }

  function acceptPolishResult(entry: Education) {
    const polished = polishResults[entry.id];
    if (!polished) return;
    updateEducation.mutate(
      {
        id: entry.id,
        institution: entry.institution,
        degree: entry.degree ?? '',
        field_of_study: entry.field_of_study ?? '',
        start_date: entry.start_date ?? '',
        end_date: entry.end_date ?? '',
        gpa: entry.gpa ?? '',
        description: polished,
        is_certification: entry.is_certification ?? false,
      },
      {
        onSuccess: () => {
          setPolishResults((prev) => {
            const next = { ...prev };
            delete next[entry.id];
            return next;
          });
        },
      },
    );
  }

  function dismissPolishResult(entryId: string) {
    setPolishResults((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
  }

  const formDescription = formData.description ?? '';
  const formHasAiInput =
    !!formData.institution.trim() ||
    !!(formData.degree ?? '').trim() ||
    !!formDescription.trim();

  function handleDelete(id: string) {
    setDeletingId(id);
    deleteEducation.mutate(id, {
      onSuccess: () => {
        setDeletingId(null);
        if (editingId === id) resetForm();
      },
      onError: () => setDeletingId(null),
    });
  }

  const isPending = createEducation.isPending || updateEducation.isPending;

  const educationForm = (
    <FormCard className={editingId ? 'mt-0 sm:mt-0' : undefined}>
      {/* Certification toggle */}
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => setIsCertification(false)}
          className={`rounded-md px-3 py-1.5 text-[14px] font-medium transition-colors ${
            !isCertification
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          Degree / Education
        </button>
        <button
          type="button"
          onClick={() => setIsCertification(true)}
          className={`rounded-md px-3 py-1.5 text-[14px] font-medium transition-colors ${
            isCertification
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          Certification / License
        </button>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold text-foreground">
          {editingId
            ? isCertification ? 'Edit Certification' : 'Edit Education'
            : isCertification ? 'New Certification' : 'New Education'}
        </h2>
        <button
          type="button"
          onClick={handleCloseForm}
          className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Close form"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {(createEducation.error ?? updateEducation.error) && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {(createEducation.error ?? updateEducation.error)?.message}
        </div>
      )}

      <form
        className="mt-4 space-y-4"
        onSubmit={handleSubmit}
        noValidate
      >
        {/* Row 1: Institution */}
        <div>
          <label
            htmlFor="edu-institution"
            className="mb-1.5 block text-[14px] font-medium text-foreground"
          >
            {isCertification ? 'Issuing Organization' : 'Institution'}
          </label>
          <input
            id="edu-institution"
            type="text"
            value={formData.institution}
            onChange={(e) => handleChange('institution', e.target.value)}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder={isCertification ? 'AWS, PMI, Google...' : 'Stanford University'}
            aria-invalid={!!fieldErrors.institution}
            aria-describedby={
              fieldErrors.institution ? 'edu-institution-error' : undefined
            }
          />
          {fieldErrors.institution && (
            <p
              id="edu-institution-error"
              className="mt-1 text-[13px] text-destructive"
            >
              {fieldErrors.institution}
            </p>
          )}
        </div>

        {/* Row 2: Degree + Field of Study */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="edu-degree"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              {isCertification ? 'Certification Name' : 'Degree'}{' '}
              {!isCertification && (
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              )}
            </label>
            <input
              id="edu-degree"
              type="text"
              value={formData.degree}
              onChange={(e) => handleChange('degree', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder={isCertification ? 'AWS Solutions Architect' : 'Bachelor of Science'}
            />
          </div>

          <div>
            <label
              htmlFor="edu-field"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Field of Study{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <input
              id="edu-field"
              type="text"
              value={formData.field_of_study}
              onChange={(e) =>
                handleChange('field_of_study', e.target.value)
              }
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Computer Science"
            />
          </div>
        </div>

        {/* Row 3: Start Date + End Date + GPA */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label
              htmlFor="edu-start-date"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Start Date{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <input
              id="edu-start-date"
              type="text"
              placeholder="e.g. 2020, Jun 2020, 2020-06-15"
              value={formData.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              aria-invalid={!!fieldErrors.start_date}
              aria-describedby={
                fieldErrors.start_date ? 'edu-start-error' : undefined
              }
            />
            {fieldErrors.start_date && (
              <p
                id="edu-start-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.start_date}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="edu-end-date"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              End Date{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <input
              id="edu-end-date"
              type="text"
              placeholder="e.g. 2024, Dec 2024, 2024-12-31"
              value={formData.end_date}
              onChange={(e) => handleChange('end_date', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              aria-invalid={!!fieldErrors.end_date}
              aria-describedby={
                fieldErrors.end_date ? 'edu-end-error' : undefined
              }
            />
            {fieldErrors.end_date && (
              <p
                id="edu-end-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.end_date}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="edu-gpa"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              GPA{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <input
              id="edu-gpa"
              type="text"
              value={formData.gpa}
              onChange={(e) => handleChange('gpa', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="3.8 / 4.0"
            />
          </div>
        </div>

        {/* Row 4: Description */}
        <div>
          <FieldHeader htmlFor="edu-description" label="Description" hint="(optional)">
            <button
              type="button"
              disabled={!formHasAiInput || formPolishing}
              onClick={runFormPolish}
              className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-[12px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
              title={
                formDescription.trim()
                  ? 'AI polish this description'
                  : 'Draft a description with AI from the degree, field and institution'
              }
            >
              {formPolishing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              {formDescription.trim() ? 'AI Polish' : 'Draft with AI'}
            </button>
          </FieldHeader>
          <textarea
            id="edu-description"
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="Relevant coursework, honors, activities..."
          />
          <AiSuggestionBox
            className="mt-2"
            loading={formPolishing}
            result={formPolishResult}
            onAccept={() => {
              if (!formPolishResult) return;
              handleChange('description', formPolishResult);
              setFormPolishResult(null);
            }}
            onRegenerate={runFormPolish}
            onDismiss={() => {
              setFormPolishResult(null);
              setFormPolishing(false);
            }}
          />
          {polishEducation.error && !formPolishing && (
            <p className="mt-1 text-[13px] text-destructive">
              {polishEducation.error.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border pt-4">
          <button
            type="submit"
            disabled={isPending || (!!editingId && !unsaved.hasChanges)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[15px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {editingId ? 'Update' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCloseForm}
            className="rounded-md px-4 py-2 text-[15px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </FormCard>
  );

  const entries = (education.data ?? []) as Education[];
  const degrees = entries.filter((e) => !e.is_certification);
  const certs = entries.filter((e) => e.is_certification);

  const allIds = useMemo(() => entries.map((e) => e.id), [entries]);
  const bulk = useBulkSelection(allIds);
  const inSelectionMode = !bulk.noneSelected;

  const bulkActions: BulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        label: 'Delete',
        icon: Trash2,
        destructive: true,
        onExecute: async (ids) => {
          await bulkDeleteEducation.mutateAsync(ids);
        },
      },
    ],
    [bulkDeleteEducation],
  );

  if (education.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (education.error) {
    return (
      <div>
        <PageHeader title="Education" />
        <div
          className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          Failed to load education entries. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Education"
        description="Your educational background: degrees, certifications, and institutions."
      >
        {!isFormOpen && (
          <button
            type="button"
            onClick={openCreateForm}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <Plus className="h-4 w-4" />
            Add Education
          </button>
        )}
      </PageHeader>

      {/* Bulk action bar */}
      {entries.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <input
              type="checkbox"
              checked={bulk.allSelected}
              ref={(el) => {
                if (el) el.indeterminate = bulk.someSelected;
              }}
              onChange={bulk.toggleAll}
              className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-foreground"
            />
            {inSelectionMode
              ? `${bulk.count} of ${entries.length}`
              : 'Select all'}
          </label>
          <BulkActionBar
            selectedCount={bulk.count}
            selectedIds={bulk.selectedArray}
            actions={bulkActions}
            onDeselectAll={bulk.deselectAll}
          />
        </div>
      )}

      {/* Create form — the edit form renders inline in place of the entry being edited */}
      {isFormOpen && !editingId && educationForm}

      {/* Education + Certifications list */}
      {entries.length === 0 && !isFormOpen ? (
        <div className="mt-6 rounded-lg border border-dashed border-border p-12 text-center">
          <GraduationCap className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-[15px] text-muted-foreground">
            No education entries yet. Add your degrees, certifications, and
            institutions.
          </p>
          <button
            type="button"
            onClick={openCreateForm}
            className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-primary transition-colors hover:text-primary/80"
          >
            <Plus className="h-3.5 w-3.5" />
            Add your first entry
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-6">
          {/* Degrees */}
          {degrees.length > 0 && (
            <div className="space-y-3">
              <h2 className="flex items-center gap-2 text-[14px] font-medium uppercase tracking-wider text-muted-foreground">
                <GraduationCap className="h-4 w-4" />
                Education
              </h2>
              {degrees.map((entry) =>
                editingId === entry.id ? (
                  <div key={entry.id} ref={editFormRef}>{educationForm}</div>
                ) : (
                  <EducationCard
                    key={entry.id}
                    entry={entry}
                    onEdit={openEditForm}
                    onDelete={handleDelete}
                    onPolish={handlePolish}
                    onAcceptPolish={acceptPolishResult}
                    onDismissPolish={dismissPolishResult}
                    polishResult={polishResults[entry.id]}
                    hasPolishResult={entry.id in polishResults}
                    deletingId={deletingId}
                    selected={bulk.isSelected(entry.id)}
                    onToggleSelect={() => bulk.toggle(entry.id)}
                    inSelectionMode={inSelectionMode}
                  />
                ),
              )}
            </div>
          )}

          {/* Certifications */}
          {certs.length > 0 && (
            <div className="space-y-3">
              <h2 className="flex items-center gap-2 text-[14px] font-medium uppercase tracking-wider text-muted-foreground">
                <Award className="h-4 w-4" />
                Certifications
              </h2>
              {certs.map((entry) =>
                editingId === entry.id ? (
                  <div key={entry.id} ref={editFormRef}>{educationForm}</div>
                ) : (
                  <EducationCard
                    key={entry.id}
                    entry={entry}
                    onEdit={openEditForm}
                    onDelete={handleDelete}
                    onPolish={handlePolish}
                    onAcceptPolish={acceptPolishResult}
                    onDismissPolish={dismissPolishResult}
                    polishResult={polishResults[entry.id]}
                    hasPolishResult={entry.id in polishResults}
                    deletingId={deletingId}
                    selected={bulk.isSelected(entry.id)}
                    onToggleSelect={() => bulk.toggle(entry.id)}
                    inSelectionMode={inSelectionMode}
                  />
                ),
              )}
            </div>
          )}
        </div>
      )}

      {deleteEducation.error && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
           {deleteEducation.error.message}
        </div>
      )}

      {polishEducation.error && !isFormOpen && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          AI polish failed: {polishEducation.error.message}
        </div>
      )}

      <UnsavedChangesGuard {...unsaved.dialogProps} />
    </div>
  );
}

/** Reusable card for a single education/certification entry. */
function EducationCard({
  entry,
  onEdit,
  onDelete,
  onPolish,
  onAcceptPolish,
  onDismissPolish,
  polishResult,
  hasPolishResult,
  deletingId,
  selected,
  onToggleSelect,
  inSelectionMode,
}: {
  entry: Education;
  onEdit: (entry: Education) => void;
  onDelete: (id: string) => void;
  onPolish: (entry: Education) => void;
  onAcceptPolish: (entry: Education) => void;
  onDismissPolish: (entryId: string) => void;
  polishResult: string | null | undefined;
  hasPolishResult: boolean;
  deletingId: string | null;
  selected: boolean;
  onToggleSelect: () => void;
  inSelectionMode: boolean;
}) {
  const isCert = entry.is_certification;
  const isPolishLoading = hasPolishResult && polishResult === null;
  const polishBox = (
    <AiSuggestionBox
      className="mt-2"
      loading={isPolishLoading}
      result={polishResult ?? null}
      onAccept={() => onAcceptPolish(entry)}
      onRegenerate={() => onPolish(entry)}
      onDismiss={() => onDismissPolish(entry.id)}
    />
  );
  return (
    <div className={`group rounded-lg border bg-card p-4 transition-colors hover:border-border/80 ${selected ? 'border-primary/40 bg-primary/[0.02]' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <SelectionCheckbox
            checked={selected}
            onChange={onToggleSelect}
            visible={inSelectionMode}
            ariaLabel={`Select ${entry.institution}`}
          />
          <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h3 className="text-[15px] font-semibold text-foreground">
              {isCert ? (entry.degree || entry.institution) : entry.institution}
            </h3>
            {!isCert && entry.gpa && (
              <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[12px] font-medium tabular-nums text-muted-foreground">
                GPA {entry.gpa}
              </span>
            )}
          </div>

          {isCert ? (
            entry.degree && entry.institution && (
              <p className="mt-0.5 text-[14px] text-muted-foreground">
                {entry.institution}
              </p>
            )
          ) : (
            (entry.degree || entry.field_of_study) && (
              <p className="mt-0.5 text-[14px] text-muted-foreground">
                {[entry.degree, entry.field_of_study]
                  .filter(Boolean)
                  .join(' in ')}
              </p>
            )
          )}

          {(entry.start_date || entry.end_date) && (
            <p className="mt-1 text-[13px] tabular-nums text-muted-foreground/70">
              {entry.start_date && entry.end_date && entry.start_date === entry.end_date
                ? entry.start_date
                : entry.start_date && entry.end_date
                  ? `${entry.start_date} — ${entry.end_date}`
                  : (entry.start_date ?? entry.end_date ?? '')
              }
            </p>
          )}

          {entry.description ? (
            <div className="mt-2">
              <div className="flex items-start gap-2">
                <p className="min-w-0 flex-1 whitespace-pre-line text-[14px] leading-relaxed text-muted-foreground">
                  {entry.description}
                </p>
                <button
                  type="button"
                  onClick={() => onPolish(entry)}
                  disabled={isPolishLoading}
                  className="shrink-0 rounded-md border border-ai-border/40 bg-ai-muted p-1 text-ai transition-colors hover:bg-ai hover:text-ai-foreground focus:outline-none focus:ring-2 focus:ring-ai disabled:opacity-50"
                  aria-label="AI polish description"
                  title="AI polish description"
                >
                  {isPolishLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              {polishBox}
            </div>
          ) : (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => onPolish(entry)}
                disabled={isPolishLoading}
                className="inline-flex items-center gap-1 rounded-md border border-ai-border/40 bg-ai-muted px-2.5 py-1 text-[12px] font-medium text-ai transition-colors hover:bg-ai hover:text-ai-foreground focus:outline-none focus:ring-2 focus:ring-ai disabled:opacity-50"
              >
                {isPolishLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                Draft description with AI
              </button>
              {polishBox}
            </div>
          )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => onEdit(entry)}
            className="rounded-md p-2.5 sm:p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={`Edit ${entry.institution}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <InlineDeleteConfirm
            onConfirm={() => onDelete(entry.id)}
            isDeleting={deletingId === entry.id}
            ariaLabel={`Delete ${entry.institution}`}
          />
        </div>
      </div>
    </div>
  );
}
